<script setup lang="ts">
import {
  onKeyStroke,
  watchOnce,
  useScroll,
  useResizeObserver,
} from '@vueuse/core';
import { nextTick, ref, watch } from 'vue';

import { KeyboardKeyCode, AppSlideUpTransition } from '@libs/ui';

interface ActivityDetailsLayoutProps {
  isLoading: boolean;
}
const props = defineProps<ActivityDetailsLayoutProps>();

interface ActivityDetailsLayoutEmits {
  (eventName: 'close'): void;
}

const emit = defineEmits<ActivityDetailsLayoutEmits>();

onKeyStroke(KeyboardKeyCode.Escape, () => emit('close'));

const visibleDescriptionLinesAmount = ref<number | undefined>(undefined);
const root = ref<HTMLDivElement>();
const headerRef = ref<HTMLElement>();
const leftPanelGalleryRef = ref<HTMLElement>();
const rightPanelScrollableHeader = ref<HTMLElement>();
const rightPanelScrollableDescription = ref<HTMLElement>();
const rightPanelStickyRef = ref<HTMLElement>();
const rightPanelScrollableDescriptionReadMoreButtonRef = ref<HTMLElement>();
const stickyTop = ref('0px');
const rightPanelScrollableDescriptionHeight = ref<number>();
const shouldShowReadMoreButton = ref<boolean>(false);

const rightPanelScrollableDescriptionSpacing = 12;
const rightPanelScrollableDescriptionLineHeight = 24;

const { arrivedState } = useScroll(root, {
  behavior: 'smooth',
});

const isScrollOnTop = ref(arrivedState.top);

async function calculateVisibleDescriptionLines(): Promise<void> {
  await nextTick();
  if (!rightPanelScrollableDescriptionHeight.value) {
    return;
  }

  visibleDescriptionLinesAmount.value = Math.floor(
    (rightPanelScrollableDescriptionHeight.value -
      (rightPanelScrollableDescriptionReadMoreButtonRef.value?.offsetHeight ??
        0)) /
      rightPanelScrollableDescriptionLineHeight
  );

  if (visibleDescriptionLinesAmount.value < 1) {
    visibleDescriptionLinesAmount.value = 2;
  }

  shouldShowReadMoreButton.value =
    !!rightPanelScrollableDescription.value &&
    rightPanelScrollableDescription.value.scrollHeight >
      rightPanelScrollableDescription.value.clientHeight;
}

watchOnce(headerRef, () => {
  if (headerRef.value) {
    const topMargin = 24;
    const height = headerRef.value.offsetHeight;
    stickyTop.value = `${height + topMargin}px`;
  }
});

watch(arrivedState, () => (isScrollOnTop.value = arrivedState.top));

useResizeObserver(leftPanelGalleryRef, calculateDescriptionHeight);
useResizeObserver(rightPanelScrollableHeader, calculateDescriptionHeight);
useResizeObserver(rightPanelStickyRef, calculateDescriptionHeight);

function calculateDescriptionHeight(): void {
  if (
    !leftPanelGalleryRef.value ||
    !rightPanelScrollableHeader.value ||
    !rightPanelStickyRef.value
  ) {
    return;
  }
  const height =
    leftPanelGalleryRef.value.offsetHeight -
    (rightPanelScrollableHeader.value.offsetHeight +
      rightPanelStickyRef.value.offsetHeight +
      rightPanelScrollableDescriptionSpacing * 2);
  rightPanelScrollableDescriptionHeight.value = height;

  calculateVisibleDescriptionLines();
}

defineExpose({
  root,
});
</script>

<template>
  <AppSlideUpTransition>
    <div ref="root" class="activity-details-layout">
      <div
        ref="headerRef"
        class="header-wrapper"
        :class="{ top: isScrollOnTop }"
      >
        <div class="header">
          <slot name="header" />
        </div>
      </div>
      <div class="wrapper">
        <div class="content">
          <div ref="leftPanelGalleryRef" class="left-panel-gallery">
            <slot name="left-panel-gallery" />
          </div>
          <div class="left-panel-content">
            <slot name="left-panel-content" />
          </div>
          <div class="right-panel">
            <div class="right-panel-scrollable">
              <div
                ref="rightPanelScrollableHeader"
                class="right-panel-scrollable-header"
              >
                <slot name="right-panel-scrollable-header" />
              </div>
              <div class="right-panel-scrollable-description-wrapper">
                <div
                  ref="rightPanelScrollableDescription"
                  class="right-panel-scrollable-description"
                  :class="{ loading: props.isLoading }"
                >
                  <slot name="right-panel-scrollable-description" />
                </div>
                <div
                  v-if="shouldShowReadMoreButton && !props.isLoading"
                  ref="rightPanelScrollableDescriptionReadMoreButtonRef"
                  class="right-panel-scrollable-description-read-more-button"
                >
                  <slot
                    name="right-panel-scrollable-description-read-more-button"
                  />
                </div>
              </div>
            </div>
            <div ref="rightPanelStickyRef" class="right-panel-sticky">
              <slot name="right-panel-sticky" />
            </div>
          </div>
        </div>
        <div class="footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </AppSlideUpTransition>
</template>

<style scoped lang="scss">
.activity-details-layout {
  --app-first-level-panel-width: 1130px;
  --app-first-level-panel-width-small: 930px;
  --side-padding: var(--spacing-24);

  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  // NOTE: Using dvh fixes the problem of address bar rendering over content in mobile Safari.
  // vh is here as a fallback for Firefox and old browsers, which do not support dvh.
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  z-index: var(--panel-first-level-z-index);
  background-color: var(--color-white);

  .header-wrapper {
    position: sticky;
    top: 0;
    background-color: var(--color-white);
    // NOTE: Swiper library internally sets z-index to 1 for its content.
    // Header must be above that to correctly work as sticky header.
    z-index: calc(var(--panel-first-level-z-index) + 1);
    padding: var(--spacing-32) 0 var(--spacing-8) 0;
    border-bottom: 1px solid var(--color-neutral-30);
    &.top {
      border: none;
    }
    @include for-less-or-equal-than-900-px {
      padding: var(--spacing-2) 0;
    }

    .header {
      margin: 0 auto;
      padding: 0 var(--side-padding);
      max-width: calc(
        var(--app-first-level-panel-width) - (var(--side-padding) * 2)
      );
      @include for-less-or-equal-than-1200-px {
        max-width: calc(
          var(--app-first-level-panel-width-small) - (var(--side-padding) * 2)
        );
      }
      @include for-less-or-equal-than-900-px {
        max-width: 100%;
      }
    }
  }

  .wrapper {
    position: relative;
    height: 100%;
    margin: var(--spacing-12) auto 0 auto;
    padding: 0 var(--side-padding);
    max-width: calc(
      var(--app-first-level-panel-width) - (var(--side-padding) * 2)
    );
    @include for-less-or-equal-than-1200-px {
      max-width: calc(
        var(--app-first-level-panel-width-small) - (var(--side-padding) * 2)
      );
    }
    @include for-less-or-equal-than-900-px {
      max-width: 100%;
    }

    .content {
      --right-panel-width: 430px;

      display: grid;
      grid-template-columns: auto var(--right-panel-width);
      grid-template-rows: auto auto;
      gap: var(--spacing-24);
      @include for-less-or-equal-than-900-px {
        grid-template-columns: 1fr;
        gap: var(--spacing-24) 0;
      }

      .left-panel-gallery {
        grid-column: 1 / 2;
        grid-row: 1 / 2;
      }

      .left-panel-content {
        margin-bottom: var(--spacing-32);
        grid-column: 1 / 2;
        grid-row: 2 / 3;
      }

      .right-panel {
        grid-column: 2/3;
        grid-row: 1/-1;
        .right-panel-scrollable {
          display: flex;
          flex-direction: column;
          gap: v-bind('`${rightPanelScrollableDescriptionSpacing}px`');
          .right-panel-scrollable-description-wrapper {
            height: v-bind('`${rightPanelScrollableDescriptionHeight}px`');
            display: flex;
            flex-direction: column;
            align-items: center;

            margin-bottom: v-bind(
              '`${rightPanelScrollableDescriptionSpacing}px`'
            );
            .right-panel-scrollable-description {
              &.loading {
                width: 100%;
                height: 100%;
              }

              display: -webkit-box;
              line-clamp: v-bind(visibleDescriptionLinesAmount);
              -webkit-line-clamp: v-bind(visibleDescriptionLinesAmount);
              -webkit-box-orient: vertical;
              overflow: hidden;
              text-overflow: ellipsis;

              text-align: center;

              @include font-body-m-light;

              p {
                margin: v-bind('`${rightPanelScrollableDescriptionSpacing}px`');
              }
            }
          }
        }
        .right-panel-sticky {
          position: sticky;
          top: v-bind(stickyTop);
        }
      }
    }
    .footer {
      position: sticky;
      margin: 0 auto;
      bottom: var(--spacing-16);
      max-width: 460px;
      width: 100%;
    }
  }
}

::-webkit-scrollbar-track {
  background: var(--color-off-white);
}
</style>
